import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, Area, AreaChart, YAxis } from 'recharts';
import dropdown from '../assets/dropdown.svg';
import '../styles/CloudCost.css';
import trendUp from '../assets/trend-up.svg';

interface CloudCostData {
  month: number;
  cost: number;
}

const CloudCost: React.FC = () => {
  const [percentageChange, setPercentageChange] = useState(0);
  const [isDecrease, setIsDecrease] = useState(true);
  const [activeDotIndex, setActiveDotIndex] = useState(0);
  
  // Fixed chart data with more curves, ups and downs
  const [chartData] = useState<CloudCostData[]>([
    { month: 1, cost: 1600 },  // Start low
    { month: 2, cost: 2200 },  // Big jump up
    { month: 3, cost: 1800 },  // Drop down
    { month: 4, cost: 2800 },  // Big spike up
    { month: 5, cost: 2100 },  // Drop significantly
    { month: 6, cost: 2600 },  // Rise again
    { month: 7, cost: 1900 },  // Another drop
    { month: 8, cost: 3100 },  // Peak high
    { month: 9, cost: 2400 },  // Drop from peak
    { month: 10, cost: 2900 }, // Rise again
    { month: 11, cost: 2200 }, // Drop
    { month: 12, cost: 3200 }, // End high
  ]);

  // Get current cost based on active dot position
  const currentCost = chartData[activeDotIndex]?.cost || chartData[0].cost;

  // Calculate percentage change based on current and previous dot position
  const calculatePercentageChange = (currentIndex: number) => {
    if (currentIndex === 0) return { percentage: 0, isDecrease: true };
    
    const currentCost = chartData[currentIndex].cost;
    const previousCost = chartData[currentIndex - 1].cost;
    const change = ((currentCost - previousCost) / previousCost) * 100;
    const absChange = Math.abs(change);
    const isDecrease = change < 0; // If change is negative, it's a decrease (good)
    
    return { percentage: Math.round(absChange), isDecrease };
  };

  // Animate the tip/dot moving along the line (slower and smoother)
  useEffect(() => {
    const dotInterval = setInterval(() => {
      setActiveDotIndex(prev => {
        const nextIndex = (prev + 1) % chartData.length;
        
        // Delay the value updates slightly to create a smooth transition effect
        setTimeout(() => {
          const { percentage, isDecrease } = calculatePercentageChange(nextIndex);
          setPercentageChange(percentage);
          setIsDecrease(isDecrease);
        }, 300); // 300ms delay for smooth transition
        
        return nextIndex;
      });
    }, 3000); // Increased to 3 seconds for more relaxed timing

    return () => clearInterval(dotInterval);
  }, [chartData.length]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Custom dot component for the animated tip
  const CustomDot = (props: any) => {
    const { cx, cy, index } = props;
    
    if (index === activeDotIndex) {
      return (
        <g>
          {/* Outer ring */}
          <circle 
            cx={cx} 
            cy={cy} 
            r="6" 
            fill="none" 
            stroke="#12B76A" 
            strokeWidth="2"
            opacity="0.6"
          />
          {/* Inner dot */}
          <circle 
            cx={cx} 
            cy={cy} 
            r="3" 
            fill="#12B76A"
          />
        </g>
      );
    }
    return null;
  };

  return (
    <div className="cloud-cost">
      <div className="cloud-cost-header">
        <p className="cloud-cost-title">Cloud Cost</p>
        <img src={dropdown} alt="Cloud Cost options" />
      </div>
      
      <div className="cloud-cost-content">
        <div>
          <div className="cloud-cost-stats-value">{formatCurrency(currentCost)}</div>
          <div className="cloud-cost-stats-change">
            {isDecrease ? (
              <img src={trendUp} alt="Trend up (cost decrease)" />
            ) : (
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 12 12" 
                fill="none" 
                className="cloud-cost-stats-change-icon"
              >
                <path 
                  d="M6 9L2 5h8l-4 4z" 
                  fill="#EF4444"
                />
              </svg>
            )}
            <span 
              className="cloud-cost-stats-change-value"
              style={{ color: isDecrease ? '#12B76A' : '#EF4444' }}
            >
              {percentageChange}%
            </span>
            <span className="cloud-cost-stats-change-text">
              {isDecrease ? 'less' : 'more'} vs last point
            </span>
          </div>
        </div>
        
        <div className="cloud-cost-chart">
          <ResponsiveContainer width="100%" height={60}>
            <AreaChart 
              data={chartData} 
              margin={{ top: 10, right: 10, left: 10, bottom: 10 }}
            >
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#12B76A" stopOpacity={0.3}/>
                  <stop offset="100%" stopColor="#12B76A" stopOpacity={0.05}/>
                </linearGradient>
              </defs>
              <YAxis hide domain={['dataMin - 200', 'dataMax + 200']} />
              <Area
                type="monotone"
                dataKey="cost"
                stroke="#12B76A"
                strokeWidth={2.5}
                fill="url(#colorGradient)"
                dot={<CustomDot />}
                activeDot={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CloudCost; 